import { useMemo } from 'react';

import { DocType } from '@/models/docs';

import { cn } from '@/lib/utils';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { SearchableCombobox } from '@/components/ui/searchable-combobox';
import type { SearchableComboboxItem } from '@/components/ui/searchable-combobox';

import { UnsureBadge } from './ClaudeDraftBanner';
import { DocFieldsLine } from './DocFieldsLine';
import { Hint } from './Hint';
import { SourceMark } from './SourceMark';
import { RemarkField, RemarkLine } from './SplitRemark';
import { TypeCombobox } from './TypeCombobox';
import { rangeLabel } from './fileLabels';
import { DraftSplit } from './pageSplitPrefill';
import type { DeleteTarget, FixOffer, FixOption } from './splitRules';
import { suggestType, typeMatches } from './typeSearch';
import { useConfirmNewDocType } from './useConfirmNewDocType';
import {
    NO_TYPE_VALUE,
    flagCustomDocType,
    isUnnamedDocType,
    labelWithAbbreviation,
    withNoTypeOption,
} from './useDocTypeOptions';

type ComboboxItems = SearchableComboboxItem[];

const KNOWN_DOC_TYPES = new Set<string>(Object.values(DocType));

/** Appends the abbreviation to known types so the combobox shows and searches "Name (ABBR)"; custom types are flagged as not standard. */
function withAbbreviation(
    item: SearchableComboboxItem,
): SearchableComboboxItem {
    if (isUnnamedDocType(item)) return flagCustomDocType(item);
    return {
        ...item,
        label: labelWithAbbreviation(item.label),
    };
}

/** A whole 1-based page number within the file, as typed in a Start or End input. */
export function isShowablePage(value: string, maxPage: number | null): boolean {
    const page = Number(value);
    return (
        value.trim() !== '' &&
        Number.isInteger(page) &&
        page >= 1 &&
        (maxPage === null || page <= maxPage)
    );
}

function FieldErrors({ messages }: { messages?: string[] }) {
    if (!messages?.length) return null;
    return (
        <div role="alert">
            {messages.map((m) => (
                <p key={m} className="mt-1 text-sm text-red-700">
                    {m}
                </p>
            ))}
        </div>
    );
}

export interface PageSplitRowProps {
    row: DraftSplit;
    /** 0-based position in the list. */
    index: number;
    isExpanded: boolean;
    onToggle: () => void;
    /** Messages to show for this row, by field; empty until the operator touches the row. */
    rowErrors: Partial<Record<'startPage' | 'endPage' | 'docType', string[]>>;
    claudeUnsure: boolean;
    /** Who drafted the saved split this row matches: 'pipeline', 'claude' or null for a person. */
    draftedBy?: string | null;
    /** Username of the person who last created or changed the saved split, when known. */
    editedBy?: string | null;
    /** Username of the person who verified the saved split, when known. */
    checkedBy?: string | null;
    /** The split's colour, shared with its pages in the viewer. */
    color: string;
    /** The saved document for these pages was made from other pages. */
    docStale?: boolean;
    /** Set while the person is choosing where a deleted split's pages go. */
    deleteChoice?: {
        previous: number | null;
        next: number | null;
        previousLabel: string;
        nextLabel: string;
        onChoose: (target: DeleteTarget) => void;
        onCancel: () => void;
    } | null;
    /** Ringed in red after a failed save so the operator can find the row. */
    isFlagged?: boolean;
    docTypes: ComboboxItems;
    folderOptions: ComboboxItems;
    foldersLoading: boolean;
    folderEmptyMessage?: string;
    maxPage: number | null;
    ingestedFileId: number;
    /** Id of the saved document these exact pages already make, if any. */
    matchedDocId: number | null;
    /** Fields filled and asked for on the matched document; 0 and 0 when its type has none. */
    fieldsFilled?: number;
    fieldsTotal?: number;
    onEdit: (patch: Partial<DraftSplit>) => void;
    /** The PDF page now in view (1-based); Start and End can take it. */
    currentPage?: number | null;
    /** Ways to close the gap or overlap between this split and the next; null when there is none. */
    fixOffer?: FixOffer | null;
    onFix?: (option: FixOption) => void;
    /** Ticks or clears this split's own Verified mark; leaves the other rows alone. */
    onCheckedChange: (checked: boolean) => void;
    onRemove: () => void;
    onShowPage?: (page: number) => void;
}

/** A split's type as shown on its row: "Full label (ABBR)" for a named type, just the label for an unnamed custom one. */
export function splitTypeLabel(
    docTypes: ComboboxItems,
    docType: string,
): string {
    const item = docTypes.find((entry) => entry.value === docType);
    const label = item?.label ?? docType;
    return KNOWN_DOC_TYPES.has(docType) || (item && !isUnnamedDocType(item))
        ? labelWithAbbreviation(label)
        : label;
}

const fieldCls = 'h-9 px-2 text-sm';
const smallButtonCls = 'h-8 text-xs';

/** One split as a card: a collapsed summary line that expands in place into its editable fields. */
export function PageSplitRow({
    row,
    index,
    isExpanded,
    onToggle,
    rowErrors,
    claudeUnsure,
    draftedBy = null,
    editedBy = null,
    checkedBy = null,
    color,
    docStale = false,
    deleteChoice = null,
    isFlagged = false,
    docTypes,
    folderOptions,
    foldersLoading,
    folderEmptyMessage,
    maxPage,
    ingestedFileId,
    matchedDocId,
    fieldsFilled = 0,
    fieldsTotal = 0,
    onEdit,
    currentPage = null,
    fixOffer = null,
    onFix,
    onCheckedChange,
    onRemove,
    onShowPage,
}: PageSplitRowProps) {
    const showPageIfValid = (value: string) => {
        if (onShowPage && isShowablePage(value, maxPage))
            onShowPage(Number(value));
    };
    const typeLabel =
        docTypes.find((item) => item.value === row.docType)?.label ??
        row.docType;
    const typeText = splitTypeLabel(docTypes, row.docType);
    const summaryErrors = [
        ...new Set([
            ...(rowErrors.startPage ?? []),
            ...(rowErrors.endPage ?? []),
            ...(rowErrors.docType ?? []),
        ]),
    ];
    const origin =
        draftedBy === null
            ? 'Person'
            : draftedBy === 'pipeline'
              ? 'Pipeline'
              : 'Claude';
    const rangeText = rangeLabel(row.startPage || '?', row.endPage || '?');
    const rowHint = typeLabel;
    const typeItems = useMemo(() => docTypes.map(withAbbreviation), [docTypes]);
    // A type typed from scratch is only created after the operator confirms.
    const { requestCreate: requestNewDocType, dialog: newDocTypeDialog } =
        useConfirmNewDocType(docTypes, 'PageSplitEditor');
    const who = row.checked
        ? `Verified by ${checkedBy ?? 'a person'}`
        : origin === 'Claude'
          ? 'Drafted by Claude, not checked yet'
          : origin === 'Pipeline'
            ? 'Made by the pipeline, not checked yet'
            : `Edited by ${editedBy ?? 'a person'} · not verified`;

    return (
        <li
            className={cn(
                'rounded-xl border bg-white',
                isExpanded ? 'border-[#CBE1E1]' : 'border-[#E2F0F0]',
                claudeUnsure && 'bg-amber-50',
                summaryErrors.length > 0 &&
                    'border-[1.5px] border-red-600 bg-[#FFFBFB]',
                isFlagged && 'ring-2 ring-red-600',
                'border-l-[5px]',
            )}
            style={{ borderLeftColor: color }}
            data-row-key={row.key}
            data-testid="PageSplitEditor__row"
        >
            <div className="flex items-center gap-3 px-3 py-2">
                <Hint text={rowHint} className="flex min-w-0 flex-1">
                    <button
                        type="button"
                        className="flex min-h-8 min-w-0 flex-1 items-center gap-3 text-left text-sm"
                        aria-expanded={isExpanded}
                        aria-label={rangeText}
                        onClick={onToggle}
                        data-testid="PageSplitEditor__rowToggle"
                    >
                        <span
                            className="shrink-0 whitespace-nowrap font-medium tabular-nums"
                            data-testid="PageSplitEditor__rowRange"
                            data-start={row.startPage || '?'}
                            data-end={row.endPage || '?'}
                        >
                            {rangeText}
                        </span>
                        <span
                            className={cn(
                                'min-w-0 truncate text-[13px]',
                                typeLabel ? 'text-slate-600' : 'text-slate-500',
                            )}
                            data-testid="PageSplitEditor__rowType"
                        >
                            {typeText || 'No type'}
                        </span>
                        {summaryErrors.length > 0 && (
                            <span className="whitespace-nowrap text-xs font-semibold text-red-600">
                                {summaryErrors.length === 1
                                    ? '1 problem'
                                    : `${summaryErrors.length} problems`}
                            </span>
                        )}
                        {docStale && (
                            <span
                                className="shrink-0 whitespace-nowrap rounded-full bg-amber-50 px-2 text-[11px] text-amber-800"
                                title="These pages changed after documents were created. Press Create documents to update it."
                                data-testid="PageSplitRow__staleTag"
                            >
                                Old pages
                            </span>
                        )}
                        {claudeUnsure && <UnsureBadge />}
                    </button>
                </Hint>
                <SourceMark
                    origin={origin}
                    checked={row.checked}
                    editedBy={editedBy}
                    checkedBy={checkedBy}
                    testId="ClaudeDraft__sourceMark"
                />
                <Hint
                    text="Changing a document's pages or type clears its Verified tick."
                    className="inline-flex"
                >
                    <Checkbox
                        checked={row.checked}
                        onCheckedChange={(value) =>
                            onCheckedChange(value === true)
                        }
                        aria-label={`Verified, ${rangeText.toLowerCase()}`}
                        data-testid="PageSplitEditor__rowVerified"
                    />
                </Hint>
            </div>

            <DocFieldsLine
                ingestedFileId={ingestedFileId}
                docId={matchedDocId}
                stale={docStale}
                filled={fieldsFilled}
                total={fieldsTotal}
                testId="PageSplitEditor__fieldsLine"
                quiet
            />

            {!isExpanded && row.remark.trim() !== '' && (
                <RemarkLine
                    remark={row.remark.trim()}
                    onOpen={onToggle}
                    testId="PageSplitEditor__remarkLine"
                />
            )}

            {fixOffer && (
                <div
                    className="flex flex-wrap items-center gap-2 px-3 pb-3 text-sm text-slate-600"
                    data-testid="PageSplitEditor__fix"
                >
                    <span>{fixOffer.problem}</span>
                    {fixOffer.options.map((option) => (
                        <Button
                            key={option.label}
                            type="button"
                            variant="outline"
                            size="sm"
                            className={cn(
                                smallButtonCls,
                                'border-[#32ACAC] text-[#1E3B3B]',
                            )}
                            onClick={() => onFix?.(option)}
                        >
                            {option.label}
                        </Button>
                    ))}
                </div>
            )}

            {!isExpanded && summaryErrors.length > 0 && (
                <ul
                    className="space-y-1 px-3 pb-3"
                    data-testid="PageSplitEditor__rowErrors"
                >
                    {summaryErrors.map((message) => (
                        <li key={message} className="text-sm text-red-700">
                            {message}
                        </li>
                    ))}
                </ul>
            )}

            {isExpanded && (
                <div
                    className="grid grid-cols-2 gap-3 px-3 pb-3"
                    data-testid="PageSplitEditor__rowEditor"
                >
                    <div className="flex flex-col gap-1 text-xs text-slate-600">
                        <Hint
                            text="Start and end pages are included."
                            className="self-start"
                        >
                            <label htmlFor={`start-${row.key}`}>
                                Start page
                            </label>
                        </Hint>
                        <Input
                            id={`start-${row.key}`}
                            type="number"
                            min={1}
                            value={row.startPage}
                            aria-label="Start page"
                            className={cn(
                                fieldCls,
                                rowErrors.startPage && 'border-red-600',
                            )}
                            aria-invalid={!!rowErrors.startPage}
                            onFocus={() => showPageIfValid(row.startPage)}
                            onClick={() => showPageIfValid(row.startPage)}
                            onChange={(e) =>
                                onEdit({ startPage: e.target.value })
                            }
                        />
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className={cn(smallButtonCls, 'self-start')}
                            disabled={currentPage === null}
                            onClick={() =>
                                currentPage !== null &&
                                onEdit({ startPage: String(currentPage) })
                            }
                            data-testid="PageSplitEditor__useStartPage"
                        >
                            Use current page
                            {currentPage === null
                                ? ''
                                : ` (${currentPage})`}{' '}
                            <span className="text-slate-500">[</span>
                        </Button>
                    </div>
                    <div className="flex flex-col gap-1 text-xs text-slate-600">
                        <Hint
                            text="Start and end pages are included."
                            className="self-start"
                        >
                            <label htmlFor={`end-${row.key}`}>End page</label>
                        </Hint>
                        <Input
                            id={`end-${row.key}`}
                            type="number"
                            min={1}
                            value={row.endPage}
                            aria-label="End page"
                            className={cn(
                                fieldCls,
                                rowErrors.endPage && 'border-red-600',
                            )}
                            aria-invalid={!!rowErrors.endPage}
                            onFocus={() => showPageIfValid(row.endPage)}
                            onClick={() => showPageIfValid(row.endPage)}
                            onChange={(e) =>
                                onEdit({ endPage: e.target.value })
                            }
                        />
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className={cn(smallButtonCls, 'self-start')}
                            disabled={currentPage === null}
                            onClick={() =>
                                currentPage !== null &&
                                onEdit({ endPage: String(currentPage) })
                            }
                            data-testid="PageSplitEditor__useEndPage"
                        >
                            Use current page
                            {currentPage === null
                                ? ''
                                : ` (${currentPage})`}{' '}
                            <span className="text-slate-500">]</span>
                        </Button>
                    </div>
                    {summaryErrors.length > 0 && (
                        <div className="col-span-2 flex flex-col text-sm text-red-700">
                            <FieldErrors messages={rowErrors.startPage} />
                            <FieldErrors messages={rowErrors.endPage} />
                        </div>
                    )}
                    <div className="col-span-2 flex flex-col gap-1 text-xs text-slate-600">
                        <span>Document type</span>
                        <TypeCombobox
                            items={withNoTypeOption(typeItems, !!row.docType)}
                            selectedValue={row.docType}
                            onSelect={(v) =>
                                onEdit({
                                    docType: v === NO_TYPE_VALUE ? '' : v,
                                })
                            }
                            allowClear={false}
                            filter={(label, search) =>
                                typeMatches(search, label) ? 1 : 0
                            }
                            renderEmpty={({ query, select, close }) => {
                                const suggestion = suggestType(
                                    query,
                                    typeItems,
                                );
                                return (
                                    <div
                                        className="flex flex-col items-start gap-2 px-3 py-2 text-left text-sm"
                                        data-testid="PageSplitEditor__noTypeMatch"
                                    >
                                        <span className="text-slate-600">
                                            No type matches “{query}”.
                                        </span>
                                        {suggestion && (
                                            <Button
                                                type="button"
                                                variant="outline"
                                                size="sm"
                                                className="h-8 max-w-full justify-start border-[#32ACAC] bg-[#EFF9F9] text-sm font-normal"
                                                onClick={() =>
                                                    select(suggestion.value)
                                                }
                                                data-testid="PageSplitEditor__typeSuggestion"
                                            >
                                                <span className="truncate">
                                                    Did you mean{' '}
                                                    <strong>
                                                        {suggestion.label}
                                                    </strong>
                                                    ?
                                                </span>
                                            </Button>
                                        )}
                                        <button
                                            type="button"
                                            className="text-xs text-slate-600 underline"
                                            onClick={() => {
                                                close();
                                                requestNewDocType(
                                                    query,
                                                    (docType) =>
                                                        onEdit({ docType }),
                                                );
                                            }}
                                            data-testid="PageSplitEditor__createType"
                                        >
                                            Create a new type “{query}” instead…
                                            (asks you to confirm)
                                        </button>
                                    </div>
                                );
                            }}
                            placeholder="Select type"
                            searchPlaceholder="Search doc types…"
                            testId={`PageSplitEditor__docType-${row.key}`}
                            showValueWhenUnmatched
                            aria-invalid={!!rowErrors.docType}
                        />
                        <FieldErrors messages={rowErrors.docType} />
                    </div>
                    <div className="col-span-2 flex flex-col gap-1 text-xs text-slate-600">
                        <span>Folder</span>
                        <SearchableCombobox
                            items={folderOptions}
                            isLoading={foldersLoading}
                            emptyMessage={folderEmptyMessage}
                            selectedValue={row.folder}
                            onSelect={(v) => onEdit({ folder: v })}
                            placeholder="Select folder"
                            searchPlaceholder="Search by code or name…"
                            testId={`PageSplitEditor__folder-${row.key}`}
                            showValueWhenUnmatched
                        />
                    </div>
                    <div className="col-span-2">
                        <RemarkField
                            id={`remark-${row.key}`}
                            value={row.remark}
                            noun="document"
                            onChange={(remark) => onEdit({ remark })}
                            testId="PageSplitEditor__remark"
                        />
                    </div>
                    <div className="col-span-2 flex flex-wrap items-center justify-between gap-2 text-xs">
                        <span className="text-slate-600">
                            {who} ·{' '}
                            {matchedDocId !== null
                                ? 'Created'
                                : 'Not created yet'}
                        </span>
                        <button
                            type="button"
                            className="text-xs text-red-600 hover:underline"
                            onClick={onRemove}
                            aria-label="Remove document"
                        >
                            Remove document
                        </button>
                    </div>
                    {deleteChoice && (
                        <div
                            className="col-span-2 flex flex-wrap items-center gap-2 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-800"
                            data-testid="PageSplitEditor__deleteConfirm"
                        >
                            <span>
                                Remove this document. Give{' '}
                                {rangeText.toLowerCase()} to:
                            </span>
                            {deleteChoice.previous !== null && (
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    className={smallButtonCls}
                                    onClick={() =>
                                        deleteChoice.onChoose('previous')
                                    }
                                    data-testid="PageSplitEditor__deleteToPrevious"
                                >
                                    {deleteChoice.previousLabel} (before)
                                </Button>
                            )}
                            {deleteChoice.next !== null && (
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    className={smallButtonCls}
                                    onClick={() =>
                                        deleteChoice.onChoose('next')
                                    }
                                    data-testid="PageSplitEditor__deleteToNext"
                                >
                                    {deleteChoice.nextLabel} (after)
                                </Button>
                            )}
                            <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                className={smallButtonCls}
                                onClick={() => deleteChoice.onChoose('none')}
                                data-testid="PageSplitEditor__deleteToNone"
                            >
                                No document
                            </Button>
                            <button
                                type="button"
                                className="text-xs text-slate-600"
                                onClick={deleteChoice.onCancel}
                                data-testid="PageSplitEditor__deleteCancel"
                            >
                                Cancel
                            </button>
                        </div>
                    )}
                </div>
            )}
            {newDocTypeDialog}
        </li>
    );
}
